// src/pages/AuditsListPage.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Typography, Tag, message, Space, Tooltip } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { mockAudits, mockClients } from '../data/mockData';
import { Audit } from '../types';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

const AuditsListPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.currentUser);
  const [audits, setAudits] = useState<Audit[]>(mockAudits);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  // Filtrar auditorías según el rol
  const filteredAudits = currentUser?.role === 'admin'
    ? audits
    : audits.filter(a => a.clientId === currentUser?.clientId);

  const getClientName = (clientId: string) => {
    const client = mockClients.find(c => c.id === clientId);
    return client?.companyName || 'N/A';
  };

  const getStatusTag = (status: Audit['status']) => {
    const statusConfig = {
      'pending': { color: 'default', text: 'Pendiente' },
      'in-progress': { color: 'processing', text: 'En Progreso' },
      'completed': { color: 'success', text: 'Completada' },
    };
    const config = statusConfig[status];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns: ColumnsType<Audit> = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
    },
    ...(currentUser?.role === 'admin' ? [{
      title: 'Cliente',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (clientId: string) => getClientName(clientId),
    }] : []),
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: Audit['status']) => getStatusTag(status),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, record: Audit) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/dashboard/audits/${record.id}`)}
            />
          </Tooltip>
          {currentUser?.role === 'admin' && (
            <>
              <Tooltip title="Editar">
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => handleEditAudit(record)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteAudit(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleCreateAudit = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = (values: { title: string; clientId: string }) => {
    const newAudit: Audit = {
      id: `a${audits.length + 1}`,
      title: values.title,
      clientId: values.clientId,
      status: 'pending',
      items: [],
      createdAt: new Date().toISOString(),
    };

    setAudits([...audits, newAudit]);
    message.success('Auditoría creada exitosamente');
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleEditAudit = (audit: Audit) => {
    setSelectedAudit(audit);
    editForm.setFieldsValue({
      title: audit.title,
      clientId: audit.clientId,
      status: audit.status,
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = (values: { title: string; clientId: string; status: Audit['status'] }) => {
    if (!selectedAudit) return;
    
    const updatedAudits = audits.map(a => 
      a.id === selectedAudit.id ? { ...a, ...values } : a
    );
    
    setAudits(updatedAudits);
    message.success('Auditoría actualizada exitosamente');
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedAudit(null);
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    editForm.resetFields();
    setSelectedAudit(null);
  };

  const handleDeleteAudit = (audit: Audit) => {
    modal.confirm({
      title: '¿Está seguro de eliminar esta auditoría?',
      icon: <ExclamationCircleOutlined />,
      content: `Se eliminará la auditoría "${audit.title}" y todos sus ítems asociados. Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        const updatedAudits = audits.filter(a => a.id !== audit.id);
        setAudits(updatedAudits);
        message.success('Auditoría eliminada exitosamente');
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>
          {currentUser?.role === 'admin' ? 'Gestión de Auditorías' : 'Mis Auditorías'}
        </Title>
        {currentUser?.role === 'admin' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateAudit}>
            Iniciar Nueva Auditoría
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredAudits}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {currentUser?.role === 'admin' && (
        <Modal
          title="Iniciar Nueva Auditoría"
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              label="Título de la Auditoría"
              name="title"
              rules={[{ required: true, message: 'Por favor ingrese el título' }]}
            >
              <Input placeholder="Ej: Auditoría Laboral Q1 2026" />
            </Form.Item>

            <Form.Item
              label="Cliente"
              name="clientId"
              rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
            >
              <Select placeholder="Seleccione un cliente">
                {mockClients.map(client => (
                  <Select.Option key={client.id} value={client.id}>
                    {client.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Crear Auditoría
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Modal de Edición */}
      {currentUser?.role === 'admin' && (
        <Modal
          title="Editar Auditoría"
          open={isEditModalOpen}
          onCancel={handleCancelEdit}
          footer={null}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditSubmit}
          >
            <Form.Item
              label="Título de la Auditoría"
              name="title"
              rules={[{ required: true, message: 'Por favor ingrese el título' }]}
            >
              <Input placeholder="Ej: Auditoría Laboral Q1 2026" />
            </Form.Item>

            <Form.Item
              label="Cliente"
              name="clientId"
              rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
            >
              <Select placeholder="Seleccione un cliente">
                {mockClients.map(client => (
                  <Select.Option key={client.id} value={client.id}>
                    {client.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Estado"
              name="status"
              rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
            >
              <Select placeholder="Seleccione el estado">
                <Select.Option value="pending">Pendiente</Select.Option>
                <Select.Option value="in-progress">En Progreso</Select.Option>
                <Select.Option value="completed">Completada</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancelEdit}>Cancelar</Button>
                <Button type="primary" htmlType="submit">Guardar Cambios</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {contextHolder}
    </div>
  );
};

export default AuditsListPage;
